import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageMeta from '@/components/feature/PageMeta';
import SiteLayout from '@/components/feature/SiteLayout';
import FilmCard from '@/components/feature/FilmCard';
import { categoryMeta, typeLabel } from '@/mocks/categories';
import { getFilmById, getRelated, viewCountLabel } from '@/lib/catalog';
import { useFavorites } from '@/hooks/useFavorites';

export default function DetailPage() {
  const { id } = useParams();
  const film = getFilmById(id);
  const { toggleFavorite, isFavorite } = useFavorites();
  const [showPlayTip, setShowPlayTip] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(''), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    setShowPlayTip(false);
    setToast('');
  }, [id]);

  useEffect(() => {
    if (!showPlayTip) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowPlayTip(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showPlayTip]);

  if (!film) {
    return (
      <PageMeta
        title="影视作品未找到 - 91在线视频"
        description="该影视作品可能已下线或链接有误，返回首页继续浏览最新电影、电视剧与综艺。"
        keywords="91在线视频,影视未找到"
      >
        <SiteLayout padded>
          <div className="w-full px-4 md:px-8 py-24 text-center">
            <span className="w-16 h-16 mx-auto rounded-full bg-background-100 text-foreground-400 flex items-center justify-center">
              <i className="ri-film-line text-3xl" />
            </span>
            <h1 className="mt-5 text-xl font-bold text-foreground-950">没有找到这部影视作品</h1>
            <p className="mt-2 text-sm text-foreground-500">链接可能已失效，去首页看看最新的影视推荐吧</p>
            <Link
              to="/"
              className="inline-flex mt-6 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm items-center gap-2 whitespace-nowrap"
            >
              <i className="ri-home-4-line" />
              返回首页
            </Link>
          </div>
        </SiteLayout>
      </PageMeta>
    );
  }

  const related = getRelated(film, 6);
  const meta = categoryMeta[film.type];
  const favorited = isFavorite(film.id);

  const handleFavorite = () => {
    toggleFavorite(film.id);
    setToast(favorited ? '已取消收藏' : '已加入收藏（保存在本机浏览器）');
  };

  return (
    <PageMeta
      title={`${film.title} - 91在线视频`}
      description={`${film.title}（${film.year}·${film.region}·${film.genre}）详细介绍：剧情简介、演职员信息与同类推荐，91在线视频带你了解这部${typeLabel(film.type)}作品。`}
      keywords={`${film.title},${film.title}在线观看,${film.genre},${film.region}${typeLabel(film.type)},91在线视频`}
    >
      <SiteLayout padded>
        <div className="relative w-full overflow-hidden bg-primary-950">
          <div className="absolute inset-0">
            <img
              src={film.backdrop}
              alt={`${film.title} 背景剧照`}
              title={`${film.title} ${film.genre} 背景图`}
              className="w-full h-full object-cover object-center opacity-60"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/90 to-primary-950/60" />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-background-50 to-transparent" />

          <div className="relative w-full px-4 md:px-8 py-8 md:py-12">
            <nav aria-label="面包屑" className="flex items-center flex-wrap gap-1.5 text-xs text-background-200/70">
              <Link to="/" className="hover:text-accent-300 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line text-background-200/40" />
              <Link to={meta.path} className="hover:text-accent-300 transition-colors whitespace-nowrap">
                {meta.label}
              </Link>
              <i className="ri-arrow-right-s-line text-background-200/40" />
              <span className="text-background-50 whitespace-nowrap">{film.title}</span>
            </nav>

            <div className="mt-6 flex flex-col md:flex-row gap-6 md:gap-10">
              <div className="w-[140px] md:w-[240px] shrink-0">
                <div className="w-full aspect-[2/3] rounded-lg overflow-hidden border border-background-50/20">
                  <img
                    src={film.poster}
                    alt={`${film.title} 主海报`}
                    title={`${film.title} 海报 ${film.year}`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 text-[11px]">
                  <span className="px-2 py-0.5 rounded-md bg-accent-500 text-primary-950 font-semibold whitespace-nowrap">
                    {typeLabel(film.type)}
                  </span>
                  <span className="px-2 py-0.5 rounded-md bg-background-50/15 text-background-50 whitespace-nowrap">
                    {film.genre}
                  </span>
                  <span className="px-2 py-0.5 rounded-md bg-background-50/15 text-background-50 whitespace-nowrap">
                    HD 高清演示
                  </span>
                </div>

                <h1 className="mt-3 text-2xl md:text-4xl font-black text-background-50 leading-tight">{film.title}</h1>
                <p className="mt-2 text-sm md:text-base text-accent-300">{film.tagline}</p>

                <dl className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl">
                  {[
                    { label: '评分', value: film.rating.toFixed(1), icon: 'ri-star-fill' },
                    { label: '上映年份', value: String(film.year), icon: 'ri-calendar-line' },
                    { label: '地区', value: film.region, icon: 'ri-map-pin-line' },
                    { label: '片长/集数', value: film.episodes, icon: 'ri-time-line' },
                  ].map((item) => (
                    <div key={item.label}>
                      <dt className="text-[10px] text-background-300/60 flex items-center gap-1">
                        <i className={`${item.icon} text-[10px]`} />
                        {item.label}
                      </dt>
                      <dd className="mt-1 text-sm font-semibold text-background-50 truncate">{item.value}</dd>
                    </div>
                  ))}
                </dl>

                <p className="mt-4 text-xs text-background-200/60 flex items-center gap-1.5">
                  <i className="ri-eye-line" />
                  {viewCountLabel(film.views)} · 最近更新 {film.updatedAt}
                </p>

                <div className="mt-6 md:mt-8 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setShowPlayTip(true)}
                    className="h-10 md:h-11 px-5 md:px-6 rounded-md bg-accent-500 hover:bg-accent-600 text-primary-950 text-sm font-semibold flex items-center gap-2 whitespace-nowrap transition-colors cursor-pointer"
                  >
                    <i className="ri-play-fill text-base" />
                    播放
                  </button>
                  <button
                    type="button"
                    onClick={handleFavorite}
                    className={`h-10 md:h-11 px-4 md:px-5 rounded-md text-sm flex items-center gap-2 whitespace-nowrap transition-colors cursor-pointer border ${
                      favorited
                        ? 'bg-background-50 text-primary-700 border-background-50'
                        : 'border-background-50/30 text-background-50 hover:bg-background-50/10'
                    }`}
                  >
                    <i className={`${favorited ? 'ri-heart-3-fill' : 'ri-heart-3-line'} text-base`} />
                    {favorited ? '已收藏' : '收藏'}
                  </button>
                  <Link
                    to={meta.path}
                    className="h-10 md:h-11 px-4 md:px-5 rounded-md border border-background-50/30 text-background-50 text-sm flex items-center gap-2 whitespace-nowrap hover:bg-background-50/10 transition-colors"
                  >
                    <i className="ri-folder-open-line" />
                    更多{meta.label}
                  </Link>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-1.5">
                  {film.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 rounded-md bg-secondary-900/60 text-secondary-100 text-[11px] border border-background-50/10"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-8 pt-8 pb-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 min-w-0">
            <section aria-label="剧情简介">
              <h2 className="text-lg md:text-xl font-bold text-foreground-950 flex items-center gap-2">
                <span className="w-1 h-4 rounded-full bg-accent-500" />
                剧情简介
              </h2>
              <p className="mt-4 text-sm leading-8 text-foreground-700">{film.synopsis}</p>
            </section>

            <section aria-label="演职员" className="mt-8">
              <h2 className="text-lg md:text-xl font-bold text-foreground-950 flex items-center gap-2">
                <span className="w-1 h-4 rounded-full bg-accent-500" />
                演职员（虚构示例）
              </h2>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="rounded-lg bg-background-100 border border-background-200 px-4 py-3">
                  <p className="text-[10px] text-foreground-400">导演</p>
                  <p className="mt-1 text-sm font-medium text-foreground-950 truncate">{film.director}</p>
                </div>
                {film.cast.map((name) => (
                  <div key={name} className="rounded-lg bg-background-50 border border-background-200 px-4 py-3">
                    <p className="text-[10px] text-foreground-400">演员</p>
                    <p className="mt-1 text-sm font-medium text-foreground-950 truncate">{name}</p>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-[11px] text-foreground-400">
                说明：以上演职员信息为界面演示所需的虚构内容，与任何真实人物无关。
              </p>
            </section>

            <section aria-label="观看提示" className="mt-8 rounded-lg bg-secondary-50 border border-secondary-200 px-4 py-4">
              <h2 className="text-sm font-semibold text-secondary-900 flex items-center gap-1.5">
                <i className="ri-information-line" />
                关于播放功能
              </h2>
              <p className="mt-2 text-xs leading-6 text-secondary-800">
                本站为影视内容演示站点，播放按钮仅用于展示页面交互效果，暂不提供真实视频播放能力，也不会引导跳转至任何外部播放源。
              </p>
            </section>
          </div>

          <aside className="lg:col-span-4 space-y-4">
            <div className="rounded-lg bg-background-100 border border-background-200 p-4">
              <p className="text-xs font-semibold text-foreground-700 mb-3">所属分类</p>
              <Link
                to={meta.path}
                className="flex items-center justify-between gap-2 rounded-md bg-background-50 border border-background-200 px-3 py-2.5 hover:border-accent-400 transition-colors"
              >
                <span className="flex items-center gap-2 text-sm text-foreground-900">
                  <span className="w-7 h-7 rounded-md bg-primary-500 text-background-50 flex items-center justify-center">
                    <i className={`${meta.icon} text-xs`} />
                  </span>
                  {meta.label}频道
                </span>
                <i className="ri-arrow-right-s-line text-foreground-400" />
              </Link>
            </div>

            <div className="rounded-lg bg-background-50 border border-background-200 p-4">
              <p className="text-xs font-semibold text-foreground-700 mb-3">同类标签</p>
              <div className="flex flex-wrap gap-1.5">
                {film.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="px-2.5 py-1 rounded-md bg-accent-100 text-accent-900 text-[11px] hover:bg-accent-200 transition-colors whitespace-nowrap"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg bg-primary-950 p-4 text-background-100">
              <p className="text-xs font-semibold text-background-50">喜欢这部作品？</p>
              <p className="mt-1.5 text-[11px] leading-5 text-background-200/70">
                收藏后会保存在本机浏览器中，下次打开还能快速找到。
              </p>
              <button
                type="button"
                onClick={handleFavorite}
                className={`mt-3 w-full h-9 rounded-md text-xs font-medium flex items-center justify-center gap-1.5 whitespace-nowrap cursor-pointer transition-colors ${
                  favorited ? 'bg-background-50 text-primary-700' : 'bg-accent-500 text-primary-950 hover:bg-accent-600'
                }`}
              >
                <i className={favorited ? 'ri-heart-3-fill' : 'ri-heart-3-line'} />
                {favorited ? '已加入收藏' : '加入收藏'}
              </button>
            </div>
          </aside>
        </div>

        <section aria-label="相关推荐" className="w-full px-4 md:px-8 pb-12">
          <div className="flex items-end justify-between gap-4 mb-6">
            <div>
              <h2 className="text-lg md:text-xl font-bold text-foreground-950 flex items-center gap-2">
                <span className="w-1 h-4 rounded-full bg-accent-500" />
                相关推荐
              </h2>
              <p className="mt-2 text-xs md:text-sm text-foreground-500">按分类、地区与题材标签关联推荐</p>
            </div>
            <Link
              to={`/category/${film.type}`}
              className="text-xs md:text-sm text-foreground-600 hover:text-accent-600 transition-colors whitespace-nowrap flex items-center gap-1"
            >
              查看全部
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-8">
            {related.map((item, index) => (
              <FilmCard key={item.id} film={item} index={index} />
            ))}
          </div>
        </section>

        {showPlayTip && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
            <button
              type="button"
              aria-label="关闭提示"
              onClick={() => setShowPlayTip(false)}
              className="absolute inset-0 bg-primary-950/60 cursor-default"
            />
            <div className="relative w-full max-w-sm bg-background-50 rounded-lg p-6 text-center animate-fade-up">
              <span className="w-12 h-12 mx-auto rounded-full bg-accent-100 text-accent-700 flex items-center justify-center">
                <i className="ri-play-circle-line text-2xl" />
              </span>
              <h3 className="mt-4 text-base font-bold text-foreground-950">暂不支持播放功能，敬请期待</h3>
              <p className="mt-2 text-xs leading-6 text-foreground-500">
                当前为影视内容演示站点，播放能力仍在准备中。你仍可以浏览详情、收藏作品或查看同类推荐。
              </p>
              <button
                type="button"
                onClick={() => setShowPlayTip(false)}
                className="mt-5 w-full h-10 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                我知道了
              </button>
            </div>
          </div>
        )}

        {toast && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] px-4 py-2.5 rounded-md bg-primary-950 text-background-50 text-xs whitespace-nowrap animate-fade-up">
            {toast}
          </div>
        )}
      </SiteLayout>
    </PageMeta>
  );
}